import * as React from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * One line that ends in an ellipsis when it doesn't fit; hovering shows the
 * whole text in a tooltip — only when it is actually cut. In a table cell,
 * give the cell `max-w-0` so the column can shrink below the text.
 *
 * @example
 * import { TableCell } from "@/components/ui/table"
 * import { TruncatedText } from "@/components/ui/truncated-text"
 *
 * export function TitleCell({ title }: { title: string }) {
 *   return (
 *     <TableCell className="max-w-64">
 *       <TruncatedText>{title}</TruncatedText>
 *     </TableCell>
 *   )
 * }
 */
function TruncatedText({ className, children, ...props }: React.ComponentProps<"span">) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const isCut = () => !!ref.current && ref.current.scrollWidth > ref.current.clientWidth;

  return (
    <Tooltip open={open} onOpenChange={(next) => setOpen(next && isCut())}>
      <TooltipTrigger asChild>
        <span ref={ref} className={cn("block truncate", className)} {...props}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm break-all">{children}</TooltipContent>
    </Tooltip>
  );
}

export { TruncatedText };
